import React, { useState } from "react";
import axios from "axios";
import { X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal for creating a new Element inside the current association's latest
// plan year. Immediately POSTs on submit and returns the fresh bundle so the
// caller can drop it into state and re-run the calc.
export default function AddElementModal({ aid, associationDisplayId, existingCategories, onCreated, onClose }) {
  const [form, setForm] = useState({
    name: "",
    element_display_id: "",  // Slice 5f — user-editable; server auto-assigns if blank
    category: "",
    description: "",
    uom: "",
    quantity: 1,
    current_cost: 0,
    useful_life_when_new: 0,
    remaining_useful_life: 0,
    included_in_plan: false,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Slice 5f — helpful placeholder showing the prefix the server will assign.
  const idPrefix = React.useMemo(() => {
    const raw = String(associationDisplayId || "").trim();
    return raw && /^\d+$/.test(raw) ? raw.padStart(3, "0") : (raw || "NNN");
  }, [associationDisplayId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (form.remaining_useful_life > form.useful_life_when_new) {
      setError("RUL cannot be greater than ULWN");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/associations/${aid}/elements`, {
        ...form,
        name: form.name.trim(),
        element_display_id: form.element_display_id.trim() || null,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        uom: form.uom.trim() || null,
        notes: form.notes.trim() || null,
      });
      onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid="add-element-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border-2 border-navy shadow-2xl w-[560px] max-h-[85vh] overflow-y-auto rounded-[8px]"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-rule bg-navy-wash">
          <div className="text-[13px] font-semibold text-navy">Add Element</div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate hover:text-coral"
            data-testid="add-element-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-4 grid grid-cols-2 gap-3 text-[12px]">
          <FieldWide label="Name *" testid="add-name">
            <input
              autoFocus
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
              className="u-input"
              data-testid="add-name-input"
            />
          </FieldWide>

          <Field label="Element ID" testid="add-element-id">
            <input
              value={form.element_display_id}
              onChange={(e) => change("element_display_id", e.target.value)}
              className="u-input font-mono"
              placeholder={`${idPrefix}.GG.EEE (auto)`}
              data-testid="add-element-id-input"
            />
          </Field>

          <Field label="Group / Category" testid="add-category">
            <input
              list="add-category-suggest"
              value={form.category}
              onChange={(e) => change("category", e.target.value)}
              className="u-input"
              placeholder="e.g. Roofing"
              data-testid="add-category-input"
            />
            <datalist id="add-category-suggest">
              {(existingCategories || []).map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>

          <Field label="UoM" testid="add-uom">
            <input
              value={form.uom}
              onChange={(e) => change("uom", e.target.value)}
              className="u-input"
              placeholder="each / sq.ft / …"
              data-testid="add-uom-input"
            />
          </Field>

          <Field label="Quantity" testid="add-quantity">
            <input
              type="number"
              step="0.01"
              value={form.quantity}
              onChange={(e) => change("quantity", parseFloat(e.target.value) || 0)}
              className="u-input font-mono tabular-nums text-right"
              data-testid="add-quantity-input"
            />
          </Field>

          <Field label="Cost / UoM" testid="add-cost">
            <input
              type="number"
              step="0.01"
              value={form.current_cost}
              onChange={(e) => change("current_cost", parseFloat(e.target.value) || 0)}
              className="u-input font-mono tabular-nums text-right"
              data-testid="add-cost-input"
            />
          </Field>

          <Field label="ULWN (yrs)" testid="add-ulwn">
            <input
              type="number"
              value={form.useful_life_when_new}
              onChange={(e) =>
                change("useful_life_when_new", parseInt(e.target.value, 10) || 0)
              }
              className="u-input font-mono tabular-nums text-right"
              data-testid="add-ulwn-input"
            />
          </Field>

          <Field label="RUL (yrs)" testid="add-rul">
            <input
              type="number"
              value={form.remaining_useful_life}
              onChange={(e) =>
                change("remaining_useful_life", parseInt(e.target.value, 10) || 0)
              }
              className={`u-input font-mono tabular-nums text-right ${form.remaining_useful_life > form.useful_life_when_new ? "border-coral text-coral" : ""}`}
              data-testid="add-rul-input"
            />
          </Field>

          <FieldWide label="Description" testid="add-description">
            <input
              value={form.description}
              onChange={(e) => change("description", e.target.value)}
              className="u-input"
              data-testid="add-description-input"
            />
          </FieldWide>

          <FieldWide label="Notes" testid="add-notes">
            <input
              value={form.notes}
              onChange={(e) => change("notes", e.target.value)}
              className="u-input"
              data-testid="add-notes-input"
            />
          </FieldWide>

          {/* Extended cost preview */}
          <div className="col-span-2 flex justify-between items-center text-[11px] text-slate pt-1 border-t border-rule">
            <span>
              Extended cost preview: {" "}
              <span className="font-mono tabular-nums text-ink font-semibold">
                ${(form.quantity * form.current_cost).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </span>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.included_in_plan}
                onChange={(e) => change("included_in_plan", e.target.checked)}
                data-testid="add-included-input"
              />
              <span>Included in plan</span>
            </label>
          </div>
        </div>

        {error && (
          <div
            className="px-4 py-2 text-[11.5px] text-coral bg-coral-wash border-t border-coral"
            data-testid="add-element-error"
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-rule bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-rule bg-white text-slate hover:border-navy hover:text-navy text-[12px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1 bg-navy text-white hover:bg-[#12406B] disabled:opacity-40 text-[12px] font-semibold flex items-center gap-1"
            data-testid="add-element-submit"
          >
            {busy && <Loader2 size={12} className="animate-spin" />} Create
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[10.5px] tracking-[0.08em] uppercase font-semibold text-slate">
        {label}
      </span>
      {children}
    </label>
  );
}

function FieldWide({ label, children, testid }) {
  return (
    <label className="flex flex-col gap-1 col-span-2" data-testid={testid}>
      <span className="text-[10.5px] tracking-[0.08em] uppercase font-semibold text-slate">
        {label}
      </span>
      {children}
    </label>
  );
}
